import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import "./globalConfirm.css";

// Promise-based replacement for window.confirm. Caller awaits a boolean.
//
//   const { confirm } = useConfirm();
//   const ok = await confirm({
//     title: "Delete entry?",
//     message: "This cannot be undone.",
//     confirmLabel: "Delete",
//     destructive: true,
//   });
//   if (!ok) return;
//
// The promise resolves false on Cancel, backdrop click, or Escape key. The
// provider serializes calls — a second confirm() while one is open replaces
// the first (the previous promise resolves false). Keeps state simple and
// avoids stacked modals.
const GlobalConfirmContext = createContext(null);

export function GlobalConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, options: {} });
  const resolverRef = useRef(null);

  const close = useCallback(
    (result) => {
      setState((prev) => ({ ...prev, open: false }));
      if (resolverRef.current) {
        resolverRef.current(result);
        resolverRef.current = null;
      }
    },
    []
  );

  const confirm = useCallback(
    (options = {}) => {
      // If a previous confirm is still open, resolve it false so its caller
      // unblocks. Then open the new one.
      if (resolverRef.current) {
        resolverRef.current(false);
        resolverRef.current = null;
      }
      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setState({ open: true, options });
      });
    },
    []
  );

  // Escape closes as cancel. Only listen while open.
  useEffect(() => {
    if (!state.open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state.open, close]);

  const {
    title = "Are you sure?",
    message = "",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    destructive = false,
  } = state.options;

  return (
    <GlobalConfirmContext.Provider value={{ confirm }}>
      {children}
      {state.open &&
        createPortal(
          <div
            className="bdr-confirm-backdrop"
            onClick={() => close(false)}
          >
            <div
              className="bdr-confirm-modal bdr-grad-container-med bdr-border-std bdr-shadow-depth"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={title}
            >
              <h3 className="bdr-confirm-title">{title}</h3>
              {message && <p className="bdr-confirm-message">{message}</p>}
              <div className="bdr-confirm-actions">
                <button
                  className="bdr-confirm-btn bdr-confirm-btn-secondary"
                  onClick={() => close(false)}
                  type="button"
                >
                  {cancelLabel}
                </button>
                <button
                  className={`bdr-confirm-btn ${
                    destructive
                      ? "bdr-confirm-btn-destructive"
                      : "bdr-confirm-btn-primary"
                  }`}
                  onClick={() => close(true)}
                  type="button"
                  autoFocus
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </GlobalConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(GlobalConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within GlobalConfirmProvider");
  }
  return context;
}
